'use client';

import { useEffect } from 'react';

/**
 * Native-only bootstrap that dismisses the Capacitor splash screen even when
 * a route bypasses the tenant-specific CapacitorProvider during local dev.
 *
 * When the WebView loads from a remote dev server (http://192.168.1.10:3000),
 * the Capacitor bridge JS injection can be delayed. We poll for the bridge
 * to appear on `window.Capacitor` before attempting to hide the splash.
 */
export function CapacitorGlobalBootstrap() {
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 60; // 60 × 250ms = 15 seconds total

    const tryHideSplash = () => {
      attempts += 1;

      const cap = (window as unknown as Record<string, unknown>).Capacitor as
        | {
            isNativePlatform?: () => boolean;
            Plugins?: {
              SplashScreen?: {
                hide: (opts?: { fadeOutDuration?: number }) => void;
              };
            };
          }
        | undefined;

      // Bridge not injected yet — keep polling
      if (!cap?.isNativePlatform) {
        if (attempts >= maxAttempts) {
          window.clearInterval(intervalId);
        }
        return;
      }

      // Not running inside a native shell (normal browser) — stop polling
      if (!cap.isNativePlatform()) {
        window.clearInterval(intervalId);
        return;
      }

      // Set the capacitor cookie so the server knows this is native
      document.cookie = 'capacitor=1;path=/;max-age=31536000;SameSite=Lax';

      // Try to hide the splash screen
      try {
        cap.Plugins?.SplashScreen?.hide({ fadeOutDuration: 300 });
      } catch {
        // Plugin not ready yet — will retry on next interval
      }

      // Stop polling after a few successful attempts (give it extra calls
      // in case the first hide() was swallowed by a timing race)
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    };

    const intervalId = window.setInterval(tryHideSplash, 250);

    // Also try immediately and on window load
    tryHideSplash();
    window.addEventListener('load', tryHideSplash);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('load', tryHideSplash);
    };
  }, []);

  return null;
}
